import React from "react";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import AdvancedTable from "../../components/AdvancedTable";
import {
  StringComboFilter,
  NumberComboFilter,
  SelectMultipleColumnsFilter,
  StringComboSFilter,
} from "../../components/filterTypes/filterTypes";
import {
  apiGetCurrency,
  apiGetCountry,
  apiGetFcsCCDetailChannelData,
  apiGetFcsPaymentStatus,  // ⚠️ 改: 老用 apiGetPaymentStatus(MT 出金 API),FCS 出金要走自己的 API
  apiDownloadFile,
  apiGetFcsCCDetailDownloadData,
  apiGetFcsCCDetailData,
} from "../../resource/index.js";
import Storage from "../../common/lib/storage";
import DateRange from "../../common/lib/dateRange";
import * as types from '../../constants/actionTypes';
import {
  DEFAULT_FCS_CCDETAIL_STATE,
} from '../../constants/constants';
import useAPIError from "../../common/hooks/useAPIError";
import theme from '../../constants/theme';
import { XSSRecover } from "../../util/xssHelper";

const useStyles = makeStyles(() => ({
  root: {
    width: (dynamicWidth) => dynamicWidth - theme.spacing(39).replace('px', ''),
  },
}));

export default function FcsCCDetailReport() {
  const { fcsCcDetailState } = useSelector((state) => state.report);
  const dispatch = useDispatch();
  const setCountry = (val) => dispatch({ type: types.SET_COUNTRY_LIST, value: val});
  const setCCDetailChannels = (val) => dispatch({ type: types.SET_FCS_CCDETAIL_CHANNEL_LIST, value: val});
  const setCCDetailStatusList = (val) => dispatch({ type: types.SET_FCS_CCDETAIL_STATUS_LIST, value: val });
  const setSelectedCountry = (val) => dispatch({ type: types.SET_SELECTED_COUNTRIES, value: val});
  const setSelectedCCDetailChannels = (val) => dispatch({ type: types.SET_FCS_SELECTED_CCDETAIL_CHANNEL_LIST, value: val });
  const setFcsCcDetailState = (val) => dispatch({ type: types.SET_FCS_CCDETAIL_STATE, value: val });
  const setSelectedContinents = (initState) => dispatch({ type: types.SET_CONTINENTS_LIST, value: initState });
  const setUserAndLevelName = (val) => dispatch({ type: types.SET_USER_AND_LEVEL_NAME, value: val });
  const setLevelId = (val) => dispatch({ type: types.SET_LEVEL_ID, value: val });
  const setShowAll = (val) => dispatch({ type: types.SET_SHOW_ALL, value: val });
  const { addError } = useAPIError();
  const [currency, setCurrency] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [noData, setNoData] = React.useState(true);
  const [count, setCount] = React.useState(0);
  const [pageCount, setPageCount] = React.useState(0);
  const [selectedSort, setSelectedSort] = React.useState({});
  const [selectedFilter, setSelectedFilter] = React.useState({});
  const [selectedSFilter, setSelectedSFilter] = React.useState({});
  const regulator = Storage.getRegulator() ?? "";
  const { filters } = useSelector(state => state.report);

  const columns = React.useMemo(
    () =>
      [
        {
          Header: "License",
          accessor: "license",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Ticket ID",
          accessor: "ticket_id",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Agent",
          accessor: "ib_name",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Client Name",
          accessor: "user_name",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Account",
          accessor: "account",
          disableSortBy: true,
          disableFilters: false,
          Filter: NumberComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Currency",
          accessor: "currency",
          disableSortBy: false,
          Filter: ({ column }) =>
            SelectMultipleColumnsFilter({ column }, { dropdown: currency }),
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Merchant Order",
          accessor: "merchant_order",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboSFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Withdraw Amount",
          accessor: "withdraw_amount",
          disableSortBy: true,
          disableFilters: true,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Open Time",
          accessor: "create_time",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Order Channel",
          accessor: "order_channel",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Order PSP",
          accessor: "psp_name",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Deposit Order",
          accessor: "deposit_order",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboSFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "CPS Order ID",
          accessor: "cps_order_id",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboSFilter,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Refund Amount",
          accessor: "refund_amount",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Order Amount",
          accessor: "order_amount",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Order Currency",
          accessor: "order_currency",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Rate",
          accessor: "exchange_rate",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "CC Processed Time",
          accessor: "transaction_time",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "CC Last Update Time",
          accessor: "cc_update_time",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Order Status",
          accessor: "order_status",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Order Fulfillment",
          accessor: "order_fulfillment",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Fund Status",
          accessor: "fund_status",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
      ], [currency]);

  const downloadFile = (file) => {
    apiDownloadFile(file)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([
          new Uint8Array([0xEF, 0xBB, 0xBF]),
          "",
          res.data],
          { type: "text/plain;charset=utf-8" }
          ));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
  };

  const getDownloadData = () => {
    setLoading(true);
    apiGetFcsCCDetailDownloadData({
      regulator: regulator,
      sort: selectedSort,
      filter: selectedFilter ? selectedFilter : {},
      sFilter: selectedSFilter ? selectedSFilter : {},
      from: filters.fromDate || "",
      to: filters.toDate || "",
      countryCodes: filters.countryCodes || undefined,
      withdrawStatus: fcsCcDetailState.selectedFcsCCDetailStatus ? fcsCcDetailState.selectedFcsCCDetailStatus.map(r => r.value) : undefined,
      orderChannel: fcsCcDetailState.selectedFcsCCDetailChannelList ? fcsCcDetailState.selectedFcsCCDetailChannelList.map(r => r.value) : undefined,
      levelId: filters.levelId || "",
      showAll: filters.showAll,
    })
      .then((res) => {
        setLoading(false);
        if (res.status === 200) {
          if (res?.data?.data?.file) {
            downloadFile(res.data.data.file);
          } else {
            addError("No data available for download");
          }
        }
      })
      .catch(() => {
        setLoading(false);
      })
  };

  const getData = React.useCallback(async () => {
    try {
      const currency_res = (await apiGetCurrency(regulator)).data;
      setCurrency(currency_res.data.currency);

      const country_res = (await apiGetCountry(regulator)).data;
      setCountry(country_res.data.country);

      const cc_detail_channels = (await apiGetFcsCCDetailChannelData(regulator)).data;
      setCCDetailChannels(cc_detail_channels.data.creditCardDetailChannel);

      const cc_withdraw_status_list = (await apiGetFcsPaymentStatus(regulator, 2)).data;
      setCCDetailStatusList(cc_withdraw_status_list.data.paymentStatus.map(r => ({value: r.p_value, label: r.p_content})));
    } catch (err) {
      console.log(err);
      setCurrency([]);
      setCountry([]);
      setCCDetailChannels([]);
      setCCDetailStatusList([]);
    }
  }, [regulator]);

  React.useEffect(() => {
    if (regulator) {
        getData();
    }
  }, [regulator]);

  const fetchData = ({ pageSize, pageIndex, sortParams, filterParams }) => {
    if (DateRange.FindRangeError(filters.fromDate, filters.toDate)) {
      addError(DateRange.FindRangeError(filters.fromDate, filters.toDate));
      return;
    }

    const filterOptions = {
      currency: filterParams.currency || undefined,
      agent: filterParams.ib_name || undefined,
      clientName: filterParams.user_name || undefined,
      account: filterParams.account && filterParams.account["="] ? filterParams.account["="] : (filterParams.account || undefined),
      merchantOrderNumber: filterParams.merchant_order || undefined,
      depositOrderNumber: filterParams.deposit_order || undefined,
      cpsOrderNumber: filterParams.cps_order_id || undefined,
    }

    const sFilterOptions = {
      merchantOrderNumber: filterParams.merchant_order || undefined,
      depositOrderNumber: filterParams.deposit_order || undefined,
      cpsOrderNumber: filterParams.cps_order_id || undefined,
    }
    setSelectedSort(sortParams);
    setSelectedFilter(filterOptions);
    setSelectedSFilter(sFilterOptions);
    setLoading(true);
    setNoData(false);
    apiGetFcsCCDetailData({
      regulator: regulator,
      pageNo: pageIndex + 1,
      pageSize: pageSize,
      sort: sortParams,
      filter: filterOptions ? filterOptions : {},
      sFilter: sFilterOptions ? sFilterOptions : {},
      from: filters.fromDate,
      to: filters.toDate,
      countryCodes: filters.countryCodes || undefined,
      withdrawStatus: fcsCcDetailState.selectedFcsCCDetailStatus ? fcsCcDetailState.selectedFcsCCDetailStatus.map(r => r.value) : undefined,
      orderChannel: fcsCcDetailState.selectedFcsCCDetailChannelList ? fcsCcDetailState.selectedFcsCCDetailChannelList.map(r => r.value) : undefined,
      levelId: filters.levelId || "",
      showAll: filters.showAll,
    })
      .then((res) => {
        if (res.data.status === "success") {
          setData(res.data.data.fcs_cc_withdraw_detail);
          setCount(res.data.data.total);
          setPageCount(Math.ceil(res.data.data.total / pageSize));
        }else{
          setNoData(true);
          setData([]);
        }
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
        setNoData(true);
        setData([]);
      })
  }

  const [dynamicWidth, setDynamicWidth] = React.useState(window.innerWidth);
  const classes = useStyles(dynamicWidth);

  function handleResize() {
    setDynamicWidth(window.innerWidth)
  }
  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetFilter = () => {
    setSelectedCountry([]);
    setSelectedContinents([]);
    setSelectedCCDetailChannels([]);
    setFcsCcDetailState(DEFAULT_FCS_CCDETAIL_STATE);
    setUserAndLevelName("");
    setLevelId("");
    setShowAll(true);
  }

  return (
    <div className={classes.root}>
      <AdvancedTable
        columns={columns}
        data={data}
        fetchData={fetchData}
        loading={loading}
        noData={noData}
        count={count}
        pageCount={pageCount}
        getDownloadData={getDownloadData}
        resetFilter={resetFilter}
      />
    </div>
  );
}
